import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Loader2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import LanguageSelector from '../components/LanguageSelector';

// PlanBlox Logo
const PlanBloxLogo = ({ className = "h-8 w-8" }) => (
  <img 
    src="https://customer-assets.emergentagent.com/job_b5e1c113-3c1b-4c22-aa5b-d15e9e791e88/artifacts/tf4zn6ok_App%20Icon%201%20.png"
    alt="PlanBlox"
    className={className}
  />
);

const Login = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await login(email, password);
      toast.success(t('auth.welcomeBack'));
      navigate('/hub');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('auth.loginFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Language Selector - Top Right */}
      <div className="absolute top-4 right-4 z-10">
        <LanguageSelector variant="icon" />
      </div>
      
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 lg:p-12">
        <div className="w-full max-w-md space-y-8">
          <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <PlanBloxLogo className="h-10 w-10 rounded-xl" />
            <span className="text-2xl font-bold tracking-tight">PlanBlox</span>
          </Link>
          
          <Card className="border-0 shadow-lg">
            <CardHeader className="space-y-1 pb-6">
              <CardTitle className="text-2xl">{t('auth.plannerSignIn')}</CardTitle>
              <CardDescription>
                {t('auth.enterCredentials')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">{t('auth.email')}</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="login-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">{t('auth.password')}</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="pr-10"
                      data-testid="login-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      data-testid="toggle-password-visibility"
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
                <Button 
                  type="submit" 
                  className="w-full rounded-full" 
                  disabled={isLoading}
                  data-testid="login-submit-btn"
                >
                  {isLoading ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t('auth.signingIn')}</>
                  ) : (
                    t('auth.signIn')
                  )}
                </Button>
              </form>
              
              <div className="mt-6 text-center text-sm text-muted-foreground">
                {t('auth.dontHaveAccount')}{' '}
                <Link to="/register" className="text-primary hover:underline font-medium" data-testid="register-link">
                  {t('auth.createOne')}
                </Link>
              </div>
              
              <div className="mt-3 text-center">
                <Link 
                  to="/forgot-password"
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  data-testid="forgot-password-link"
                >
                  {t('auth.forgotPassword')}
                </Link>
              </div>
            </CardContent>
          </Card>
          
          <div className="text-center">
            <Link to="/" className="text-sm text-muted-foreground hover:text-primary transition-colors">
              ← {t('auth.backToWelcome')}
            </Link>
          </div>
        </div>
      </div>
      
      {/* Right side - Image */}
      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-primary/10 to-primary/5 items-center justify-center p-12">
        <div className="max-w-lg text-center space-y-6">
          <img 
            src="https://images.pexels.com/photos/35428064/pexels-photo-35428064.jpeg" 
            alt="Professional workspace"
            className="rounded-2xl shadow-2xl"
          />
          <h2 className="text-2xl font-bold text-foreground">{t('auth.eventPlannerCollaboration')}</h2>
          <p className="text-muted-foreground">
            {t('auth.coordinateAcross')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
